/**
 * Copyright (C) 2024 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import ActionCreator from '../action-creator';
import { FlightLegChartData, PlansSortDirection, SortField } from './reducer';
import MissionPlanDetails from '../../shared/entities/missionPlanDetails';
import Antenna from '../../shared/entities/antenna';
import Terminal from '../../shared/entities/terminal';
interface RequestMissionPlanActionProps {
  datapathUuid: string | null;
  userId: string;
}
interface GetMissionPlanByIdProps {
  userId: string;
  missionPlanId?: number | null;
}
interface RequestPerformanceOutlookProps {
  missionId: number | null;
}

interface DeleteMissionPlanProps {
  missionPlanId: number | null;
  userId: string;
}

interface SetChartsDataProps {
  flightLegId: number;
  data: FlightLegChartData[];
}

interface RequestHnaOverrideDataProps {
  missionId: number | null;
  userId: string;
}

export const ActionCreators = {
  RequestMissionPlans: new ActionCreator<'RequestMissionPlans', RequestMissionPlanActionProps>('RequestMissionPlans'),
  ReceivedMissionPlansSuccess: new ActionCreator<'ReceivedMissionPlansSuccess', any>('ReceivedMissionPlansSuccess'),
  ReceivedMissionPlansError: new ActionCreator<'ReceivedMissionPlansError', any>('ReceivedMissionPlansError'),
  ClearReceivedMissionPlansError: new ActionCreator<'ClearReceivedMissionPlansError', any>(
    'ClearReceivedMissionPlansError',
  ),
  SetPlansListSortDirection: new ActionCreator<'SetPlansListSortDirection', any>('SetPlansListSortDirection'),
  SetPlansListSortField: new ActionCreator<'SetPlansListSortField', any>('SetPlansListSortField'),
  SaveMissionPlan: new ActionCreator<'SaveMissionPlan', MissionPlanDetails>('SaveMissionPlan'),
  SavedMissionPlanSuccess: new ActionCreator<'SavedMissionPlanSuccess', any>('SavedMissionPlanSuccess'),
  SavedMissionPlanError: new ActionCreator<'SavedMissionPlanError', any>('SavedMissionPlanError'),
  ClearSavedMissionPlan: new ActionCreator<'ClearSavedMissionPlan', any>('ClearSavedMissionPlan'),
  ClearSavedMissionPlanError: new ActionCreator<'ClearSavedMissionPlanError', any>('ClearSavedMissionPlanError'),
  GetMissionPlanById: new ActionCreator<'GetMissionPlanById', GetMissionPlanByIdProps>('GetMissionPlanById'),
  ClearSavedMissionPlanById: new ActionCreator<'ClearSavedMissionPlanById', any>('ClearSavedMissionPlanById'),
  ReceivedMissionPlanByIdSuccess: new ActionCreator<'ReceivedMissionPlanByIdSuccess', any>(
    'ReceivedMissionPlanByIdSuccess',
  ),
  ReceivedMissionPlanByIdError: new ActionCreator<'ReceivedMissionPlanByIdError', any>('ReceivedMissionPlanByIdError'),
  ClearReceivedMissionPlanByIdError: new ActionCreator<'ClearReceivedMissionPlanByIdError', any>(
    'ClearReceivedMissionPlanByIdError',
  ),
  DeleteMissionPlan: new ActionCreator<'DeleteMissionPlan', DeleteMissionPlanProps>('DeleteMissionPlan'),
  DeletedMissionPlanSuccess: new ActionCreator<'DeletedMissionPlanSuccess', any>('DeletedMissionPlanSuccess'),
  DeletedMissionPlanError: new ActionCreator<'DeletedMissionPlanError', any>('DeletedMissionPlanError'),
  ClearDeletedMissionPlanSuccess: new ActionCreator<'ClearDeleteMissionPlanSuccess', any>(
    'ClearDeleteMissionPlanSuccess',
  ),
  ClearDeletedMissionPlanError: new ActionCreator<'ClearDeletedMissionPlanError', any>('ClearDeletedMissionPlanError'),
  SetSelectedAntenna: new ActionCreator<'SetSelectedAntenna', Antenna>('SetSelectedAntenna'),
  ClearSelectedAntenna: new ActionCreator<'ClearSelectedAntenna', any>('ClearSelectedAntenna'),
  SetSelectedTerminal: new ActionCreator<'SetSelectedTerminal', Terminal>('SetSelectedTerminal'),
  ClearSelectedTerminal: new ActionCreator<'ClearSelectedTerminal', any>('ClearSelectedTerminal'),
  SetOverrideRecommendedAntenna: new ActionCreator<'SetOverrideRecommendedAntenna', boolean>(
    'SetOverrideRecommendedAntenna',
  ),
  SetMissionDetailsLoading: new ActionCreator<'SetMissionDetailsLoading', boolean>('SetMissionDetailsLoading'),
  DeleteMissionConfirmation: new ActionCreator<'DeleteMissionConfirmation', boolean>('DeleteMissionConfirmation'),
  RequestPerformanceOutlook: new ActionCreator<'RequestPerformanceOutlook', RequestPerformanceOutlookProps>(
    'RequestPerformanceOutlook',
  ),

  SetSelectedMissionPlanId: new ActionCreator<'SetSelectedMissionPlanId', number>('SetSelectedMissionPlanId'),
  ClearSelectedMissionPlanId: new ActionCreator<'ClearSelectedMissionPlanId', undefined>('ClearSelectedMissionPlanId'),
  SavePerformanceOutlookMissionPlanIdSuccessful: new ActionCreator<
    'SavePerformanceOutlookMissionPlanIdSuccessful',
    undefined
  >('SavePerformanceOutlookMissionPlanIdSuccessful'),
  SavePerformanceOutlookMissionPlanIdError: new ActionCreator<'SavePerformanceOutlookMissionPlanIdError', undefined>(
    'SavePerformanceOutlookMissionPlanIdError',
  ),
  SetFlightLegVictoryChartData: new ActionCreator<'SetFlightLegVictoryChartData', SetChartsDataProps>(
    'SetFlightLegVictoryChartData',
  ),
  DeleteFlightLegVictoryChartData: new ActionCreator<'DeleteFlightLegVictoryChartData', number>(
    'DeleteFlightLegVictoryChartData',
  ),
  RequestHnaOverrideData: new ActionCreator<'RequestHnaoverrideData', RequestHnaOverrideDataProps>(
    'RequestHnaoverrideData',
  ),
  ReceivedHnaOverrideDataSuccess: new ActionCreator<'RequestHnaOverrideDataSuccess', any>(
    'RequestHnaOverrideDataSuccess',
  ),
  ReceivedHnaOverrideDataError: new ActionCreator<'RequestHnaOverrideDataError', any>('RequestHnaOverrideDataError'),
  ClearHnaOverrideData: new ActionCreator<'ClearHnaOverrideData', any>('ClearHnaOverrideData'),
  CancelRequestHnaOverrideData: new ActionCreator<'CancelRequestHnaOverrideData', any>('CancelRequestHnaOverrideData'),
  SetMissionPlanSavedOrUpdated: new ActionCreator<'SetMissionPlanSavedOrUpdated', any>('SetMissionPlanSavedOrUpdated'),
};

export type Action = typeof ActionCreators[keyof typeof ActionCreators];

export function getMissionPlans(datapathUuid: string | null, userId: string) {
  return ActionCreators.RequestMissionPlans.create({ datapathUuid, userId });
}

export function clearReceivedMissionPlansError() {
  return ActionCreators.ClearReceivedMissionPlansError.create();
}
export function saveMissionPlan(plan: MissionPlanDetails) {
  return ActionCreators.SaveMissionPlan.create(plan);
}

export function clearSavedMissionPlan() {
  return ActionCreators.ClearSavedMissionPlan.create();
}

export function clearSavedMissionPlanError() {
  return ActionCreators.ClearSavedMissionPlanError.create();
}

export function getMissionPlanById(userId: string, missionPlanId?: number | null) {
  const payload = missionPlanId ? { missionPlanId: missionPlanId, userId: userId } : { userId: userId };
  return ActionCreators.GetMissionPlanById.create(payload);
}

export function clearSavedMissionPlanById() {
  return ActionCreators.ClearSavedMissionPlanById.create();
}

export function clearReceivedMissionPlanByIdError() {
  return ActionCreators.ClearReceivedMissionPlanByIdError.create();
}

export function deleteMissionPlan(missionPlanId: number | null, userId: string) {
  return ActionCreators.DeleteMissionPlan.create({ missionPlanId: missionPlanId, userId: userId });
}

export function deleteMissionPlanError(error: any): any {
  return ActionCreators.DeletedMissionPlanError.create(
    error.message !== undefined ? error.message : 'Error deleting mission plan',
  );
}

export function clearDeletedMissionSuccess() {
  return ActionCreators.ClearDeletedMissionPlanSuccess.create();
}

export function clearDeletedMissionError() {
  return ActionCreators.ClearDeletedMissionPlanError.create();
}

export function setSortDirection(direction: PlansSortDirection) {
  return ActionCreators.SetPlansListSortDirection.create(direction);
}

export function setSortField(field: SortField) {
  return ActionCreators.SetPlansListSortField.create(field);
}

export function setSelectedAntenna(antenna: Antenna) {
  return ActionCreators.SetSelectedAntenna.create(antenna);
}

export function clearSelectedAntenna() {
  return ActionCreators.ClearSelectedAntenna.create();
}

export function setSelectedTerminal(terminal: Terminal) {
  return ActionCreators.SetSelectedTerminal.create(terminal);
}

export function clearSelectedTerminal() {
  return ActionCreators.ClearSelectedTerminal.create();
}

export function setOverrideRecommendedAntenna(bool: boolean) {
  return ActionCreators.SetOverrideRecommendedAntenna.create(bool);
}

export function setMissionDetailsLoading(bool: boolean) {
  return ActionCreators.SetMissionDetailsLoading.create(bool);
}

export function deleteMissionConfirmation(bool: boolean) {
  return ActionCreators.DeleteMissionConfirmation.create(bool);
}

export function getPerformanceOutlook(missionId: number) {
  return ActionCreators.RequestPerformanceOutlook.create({ missionId: missionId });
}

export function setFlightLegVictoryChartData(flightLegId: number, data: FlightLegChartData[]) {
  return ActionCreators.SetFlightLegVictoryChartData.create({ flightLegId, data });
}

export function deleteFlightLegVictoryChartData(flightLegId: number) {
  return ActionCreators.DeleteFlightLegVictoryChartData.create(flightLegId);
}

export function setSelectedMissionPlanId(missionId: number) {
  return ActionCreators.SetSelectedMissionPlanId.create(missionId);
}
export function clearSelectedMissionPlanId() {
  return ActionCreators.ClearSelectedMissionPlanId.create();
}

export function getHnaOverrideData(missionId: number, userId: string) {
  return ActionCreators.RequestHnaOverrideData.create({ missionId: missionId, userId: userId });
}

export function clearHnaOverrideData() {
  return ActionCreators.ClearHnaOverrideData.create();
}
export function cancelRequestHnaOverrideData() {
  return ActionCreators.CancelRequestHnaOverrideData.create();
}
export function setMissionPlanSavedOrUpdated(isSaved: boolean) {
  return ActionCreators.SetMissionPlanSavedOrUpdated.create(isSaved);
}